import { GroupFS, RoundsFS, SubGroupTooltipAnalytics } from "../../pojo/fund-strategy-export-classes";
import { SubGroupFormGroup } from "../../pojo/fund-strategy-export-types";
import {
  FsChartsData,
  FsExitSizeChartData,
  FsAvgInvestmentChartData,
  FsProceedsByGroupChartsData,
  FsProceedsBySizeChartsData,
  ExitSizeAggregateData,
  AvgInvestmentAggregateData,
  GroupProceedsAggregateData,
  SizeProceedsAggregateData,
  FsChartsAggregatedData,
  FsPreparedRoundsData,
  FsWriteOffData,
} from "../pojo/fs-charts-classes";

export class FsChartsCalcService {

  static calculate(fsGroups: GroupFS[]): FsChartsData {
    const data = new FsChartsData();

    if (!fsGroups || fsGroups.length === 0) {
      return data;
    }

    const aggregatedData = this.collectAllDataInSinglePass(fsGroups);

    data.barExitSize = this.buildExitSizeData(aggregatedData.exitSizeMap);
    data.barAvgInv = this.buildAvgInvestmentData(aggregatedData.avgInvestmentMap, aggregatedData.writeOffData);
    data.barProceedsByGroup = this.buildProceedsByGroupData(aggregatedData.groupProceedsMap);
    data.pieProceedsBySize = this.buildProceedsBySizeData(aggregatedData.sizeProceedsMap);

    return data;
  }

  static getSubGroupTooltipAnalyticsData(subGroup: SubGroupFormGroup): SubGroupTooltipAnalytics[] {
    const results: SubGroupTooltipAnalytics[] = [];
    let cumulativeInvestment = 0;

    subGroup.value.roundsData
      .slice()
      .sort((a, b) => a.roundOrder - b.roundOrder)
      .forEach(({ investment, exitCompNumber, exitCompPrice, exitProceeds, holding }) => {
        if (investment) {
          cumulativeInvestment += investment;
        }

        if (exitCompNumber > 0 && exitCompPrice) {
          const moic = cumulativeInvestment > 0 ? exitProceeds / cumulativeInvestment : 0;

          results.push({
            exitCompanyPrice: exitCompPrice,
            exitCompNumber: exitCompNumber,
            proceeds: exitProceeds,
            holding: holding,
            moic: moic,
          });
        }
      });

    return results.sort((a, b) => b.exitCompanyPrice - a.exitCompanyPrice);
  }

  private static collectAllDataInSinglePass(fsGroups: GroupFS[]): FsChartsAggregatedData {
    const exitSizeMap = new Map<number, ExitSizeAggregateData>();
    const avgInvestmentMap = new Map<number, AvgInvestmentAggregateData>();
    const groupProceedsMap = new Map<string, GroupProceedsAggregateData>();
    const sizeProceedsMap = new Map<number, SizeProceedsAggregateData>();
    const writeOffData: FsWriteOffData = { totalInvestment: 0, totalCompanies: 0 };

    fsGroups.forEach((group) => {
      this.initializeGroupProceeds(groupProceedsMap, group.name);

      group.subGroups.forEach((subGroup) => {
        const { sortedRounds, initialHolding } = this.prepareSortedRounds(subGroup.roundsData);
        let cumulativeInvestment = 0;

        sortedRounds.forEach((round) => {
          if (round.investment) {
            cumulativeInvestment += round.investment;
          }
          this.processWriteOff(round, cumulativeInvestment, writeOffData);

          if (round.exitCompNumber && round.exitCompPrice) {
            this.processExitData(round, initialHolding, exitSizeMap, avgInvestmentMap, groupProceedsMap, sizeProceedsMap, group.name, cumulativeInvestment);
          }
        });
      });
    });

    return {
      avgInvestmentMap,
      writeOffData,
      exitSizeMap,
      sizeProceedsMap,
      groupProceedsMap,
    };
  }

  /**
   * Prepare sorted rounds and find initial holding.
   * Initial holding = holding in the first round (by roundOrder) with investment
   */
  private static prepareSortedRounds(roundsData: RoundsFS[]): FsPreparedRoundsData {
    const sortedRounds = [...roundsData].sort((a, b) => a.roundOrder - b.roundOrder);
    const initialRound = sortedRounds.find((round) => round.investment);
    const initialHolding = initialRound?.holding || 0;
    return { sortedRounds, initialHolding };
  }

  private static processWriteOff(round: RoundsFS, cumulativeInvestment: number, writeOffData: FsWriteOffData): void {
    if (round.writeOff) {
      writeOffData.totalInvestment += cumulativeInvestment;
      writeOffData.totalCompanies += round.writeOff;
    }
  }

  private static processExitData(
    round: RoundsFS,
    initialHolding: number,
    exitSizeMap: Map<number, ExitSizeAggregateData>,
    avgInvestmentMap: Map<number, AvgInvestmentAggregateData>,
    groupProceedsMap: Map<string, GroupProceedsAggregateData>,
    sizeProceedsMap: Map<number, SizeProceedsAggregateData>,
    groupName: string,
    cumulativeInvestment: number,
  ): void {
    const exitCompPrice = round.exitCompPrice;
    const exitCompNumber = round.exitCompNumber;
    const holding = round.holding || 0;
    const proceeds = round.exitProceeds || 0;

    this.updateExitSizeMap(exitSizeMap, exitCompPrice, exitCompNumber, holding, initialHolding);

    // Update Avg Investment data (only for non-writeOff exits)
    if (exitCompPrice) {
      this.updateAvgInvestmentMap(avgInvestmentMap, exitCompPrice, exitCompNumber, cumulativeInvestment, proceeds);
    }
    if (proceeds) {
      this.updateGroupProceedsMap(groupProceedsMap, groupName, exitCompNumber, proceeds, cumulativeInvestment);
    }
    if (exitCompPrice && proceeds) {
      this.updateSizeProceedsMap(sizeProceedsMap, exitCompPrice, exitCompNumber, proceeds);
    }
  }

  private static updateExitSizeMap(
    exitSizeMap: Map<number, ExitSizeAggregateData>,
    exitCompPrice: number,
    exitCompNumber: number,
    holding: number,
    initialHolding: number,
  ): void {
    const existing = exitSizeMap.get(exitCompPrice) || {
      totalCompNumber: 0,
      totalHolding: 0,
      totalInitialHolding: 0,
    };

    existing.totalCompNumber += exitCompNumber;
    existing.totalHolding += holding * exitCompNumber;
    existing.totalInitialHolding += initialHolding * exitCompNumber;

    exitSizeMap.set(exitCompPrice, existing);
  }

  private static updateAvgInvestmentMap(
    avgInvestmentMap: Map<number, AvgInvestmentAggregateData>,
    exitCompPrice: number,
    exitCompNumber: number,
    cumulativeInvestment: number,
    proceeds: number,
  ): void {
    const existing = avgInvestmentMap.get(exitCompPrice) || {
      totalCompNumber: 0,
      totalCumulativeInvestment: 0,
      totalProceeds: 0,
    };

    existing.totalCompNumber += exitCompNumber;
    existing.totalCumulativeInvestment += cumulativeInvestment * exitCompNumber;
    existing.totalProceeds += proceeds;

    avgInvestmentMap.set(exitCompPrice, existing);
  }

  private static updateGroupProceedsMap(
    groupProceedsMap: Map<string, GroupProceedsAggregateData>,
    groupName: string,
    exitCompNumber: number,
    proceeds: number,
    investment: number,
  ): void {
    const existing = groupProceedsMap.get(groupName);
    existing.totalCompNumber += exitCompNumber;
    existing.totalProceeds += proceeds;
    existing.totalInvestment += investment * exitCompNumber;
    existing.totalGain += (proceeds - investment * exitCompNumber);
  }

  private static updateSizeProceedsMap(
    sizeProceedsMap: Map<number, SizeProceedsAggregateData>,
    exitCompPrice: number,
    exitCompNumber: number,
    proceeds: number,
  ): void {
    const existing = sizeProceedsMap.get(exitCompPrice) || {
      totalCompNumber: 0,
      totalProceeds: 0,
    };

    existing.totalCompNumber += exitCompNumber;
    existing.totalProceeds += proceeds;

    sizeProceedsMap.set(exitCompPrice, existing);
  }

  private static buildExitSizeData(exitSizeMap: Map<number, ExitSizeAggregateData>): FsExitSizeChartData[] {
    const result: FsExitSizeChartData[] = [];

    exitSizeMap.forEach((data, exitCompPrice) => {
      const chartData = new FsExitSizeChartData();
      chartData.compNumber = data.totalCompNumber;
      chartData.exitCompValue = exitCompPrice;
      chartData.avgInitialHolding = data.totalCompNumber > 0 ? data.totalInitialHolding / data.totalCompNumber : 0;
      chartData.avgExitHolding = data.totalCompNumber > 0 ? data.totalHolding / data.totalCompNumber : 0;
      result.push(chartData);
    });

    return result.sort((a, b) => a.exitCompValue - b.exitCompValue);
  }

  private static buildAvgInvestmentData(
    avgInvestmentMap: Map<number, AvgInvestmentAggregateData>,
    writeOffData: { totalInvestment: number; totalCompanies: number },
  ): FsAvgInvestmentChartData[] {
    const result: FsAvgInvestmentChartData[] = [];

    avgInvestmentMap.forEach((data, exitCompPrice) => {
      const chartData = new FsAvgInvestmentChartData();
      chartData.compNumber = data.totalCompNumber;
      chartData.exitCompValue = exitCompPrice;
      chartData.avgProceeds = data.totalCompNumber ? data.totalProceeds / data.totalCompNumber : 0;
      chartData.avgInv = data.totalCompNumber ? data.totalCumulativeInvestment / data.totalCompNumber : 0;
      chartData.avgMoic = chartData.avgInv > 0 ? chartData.avgProceeds / chartData.avgInv : 0;
      chartData.isWriteOff = false;
      result.push(chartData);
    });

    // Add writeOff data if exists
    if (writeOffData.totalCompanies) {
      const writeOffChartData = new FsAvgInvestmentChartData();
      writeOffChartData.compNumber = writeOffData.totalCompanies;
      writeOffChartData.exitCompValue = null;
      writeOffChartData.avgProceeds = null;
      writeOffChartData.avgInv = writeOffData.totalCompanies ? writeOffData.totalInvestment / writeOffData.totalCompanies : 0;
      writeOffChartData.avgMoic = null;
      writeOffChartData.isWriteOff = true;
      result.push(writeOffChartData);
    }

    return result.sort((a, b) => b.exitCompValue - a.exitCompValue);
  }

  private static buildProceedsByGroupData(groupProceedsMap: Map<string, GroupProceedsAggregateData>): FsProceedsByGroupChartsData[] {
    const result: FsProceedsByGroupChartsData[] = [];

    groupProceedsMap.forEach((data) => {
      if (data.totalProceeds > 0) {
        const chartData = new FsProceedsByGroupChartsData();
        chartData.groupName = data.groupName;
        chartData.compNumber = data.totalCompNumber;
        chartData.sumProceeds = data.totalProceeds;
        chartData.investment = data.totalInvestment;
        chartData.gain = data.totalGain;
        result.push(chartData);
      }
    });

    return result.sort((a, b) => b.sumProceeds - a.sumProceeds);
  }

  private static buildProceedsBySizeData(sizeProceedsMap: Map<number, SizeProceedsAggregateData>): FsProceedsBySizeChartsData[] {
    const result: FsProceedsBySizeChartsData[] = [];

    sizeProceedsMap.forEach((data, exitCompPrice) => {
      const chartData = new FsProceedsBySizeChartsData();
      chartData.compNumber = data.totalCompNumber;
      chartData.exitCompValue = exitCompPrice;
      chartData.sumProceeds = data.totalProceeds;
      result.push(chartData);
    });

    return result.sort((a, b) => b.sumProceeds - a.sumProceeds);
  }

  private static initializeGroupProceeds(groupProceedsMap: Map<string, GroupProceedsAggregateData>, groupName: string): void {
    if (!groupProceedsMap.has(groupName)) {
      groupProceedsMap.set(groupName, { groupName, totalProceeds: 0, totalCompNumber: 0, totalInvestment: 0, totalGain: 0 });
    }
  }
}

